<script setup lang="ts">
import { HelpCircle, Plus, Minus } from 'lucide-vue-next';
import { ref } from 'vue';

const faqs = [
    {
        question: 'Est-ce que l’IA fait le travail à ma place ?',
        answer: 'Non. Rewio vous fait gagner du temps sur les tâches répétitives (premier tri, pré-remplissage de la grille), mais vous gardez toujours le dernier mot. Chaque suggestion est une proposition que vous validez, modifiez ou ignorez — votre revue reste votre travail.',
    },
    {
        question: 'Comment se passe le tri de mes articles ?',
        answer: "Vous passez vos articles un par un et décidez d'inclure ou d'exclure chacun en notant votre justification. Le diagramme PRISMA se construit automatiquement à partir de vos décisions, sans saisie supplémentaire.",
    },
    {
        question: 'Puis-je importer ma bibliothèque Zotero et mes PDF ?',
        answer: "Oui. Vous pouvez connecter votre compte Zotero pour importer vos collections et vos références, et déposer vos PDF directement dans l'outil. Rewio vous aide ensuite à lier chaque PDF à la bonne référence.",
    },
    {
        question: 'Mes données sont-elles sécurisées ?',
        answer: 'Oui, vos données sont chiffrées et stockées sur des serveurs sécurisés en Europe. Nous ne vendons jamais vos données et vous pouvez les exporter à tout moment.',
    },
    {
        question: "Puis-je utiliser l'outil pour des revues Cochrane ?",
        answer: 'Absolument. Rewio suit les standards PRISMA et Cochrane pour garantir la rigueur méthodologique nécessaire aux revues systématiques de haute qualité.',
    },
    {
        question: 'Puis-je collaborer avec mon équipe ?',
        answer: "Le plan Pro permet d'inviter des collaborateurs sur vos projets pour effectuer des tris indépendants ou croiser vos extractions de données.",
    },
];

const openIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
    openIndex.value = openIndex.value === index ? null : index;
};
</script>

<template>
    <section id="faq" class="border-t border-border/40 bg-muted/30 py-24">
        <div class="mx-auto max-w-5xl px-4">
            <div class="mb-16 text-center">
                <span
                    class="mb-4 inline-block text-xs font-medium tracking-widest text-brand uppercase"
                >
                    FAQ
                </span>
                <h2
                    class="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                >
                    Questions fréquentes
                </h2>
                <p class="mx-auto mt-4 max-w-xl text-muted-foreground">
                    Tout ce que vous devez savoir pour commencer.
                </p>
            </div>

            <div class="mx-auto max-w-3xl space-y-4">
                <div
                    v-for="(faq, index) in faqs"
                    :key="index"
                    class="overflow-hidden rounded-2xl border border-border/60 bg-card transition-all"
                >
                    <button
                        @click="toggleFaq(index)"
                        class="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-accent/40"
                    >
                        <span class="font-semibold text-foreground">{{
                            faq.question
                        }}</span>
                        <div
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted"
                        >
                            <Minus
                                v-if="openIndex === index"
                                class="h-4 w-4 text-muted-foreground"
                            />
                            <Plus
                                v-else
                                class="h-4 w-4 text-muted-foreground"
                            />
                        </div>
                    </button>

                    <div
                        class="grid transition-[grid-template-rows] duration-400 ease-in-out"
                        :class="
                            openIndex === index
                                ? 'grid-rows-[1fr]'
                                : 'grid-rows-[0fr]'
                        "
                    >
                        <div class="overflow-hidden">
                            <div
                                class="border-t border-border/60 p-6 text-muted-foreground"
                            >
                                {{ faq.answer }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-16 text-center">
                <div
                    class="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-6 py-3 shadow-sm"
                >
                    <HelpCircle class="h-5 w-5 text-brand" />
                    <span class="text-sm font-medium text-muted-foreground">
                        D'autres questions ?
                        <a href="#" class="font-bold text-brand hover:underline"
                            >Contactez notre support</a
                        >
                    </span>
                </div>
            </div>
        </div>
    </section>
</template>
